import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { GraduationCap, Mic } from 'lucide-react';
import { EducateNavHeader } from '@/components/brand/EducateNavHeader';
import { AudioTransport } from '@/components/player/AudioTransport';
import { ErrorScreen } from '@/components/StatusScreens';
import { useAudioPlayback, type AudioClip } from '@/hooks/useAudioPlayback';
import type { EducateModule } from '@/hooks/useEducateModule';
import type { PodcastContent, TextUnit } from '@/types/educate';
import { BRAND_ACTION_COLOR, PODCAST_ACTIVE_BG } from '@/constants/ui';

const ACTIVE_BG = PODCAST_ACTIVE_BG;
const ACTIVE_BORDER = BRAND_ACTION_COLOR;

const APPROX_WORDS_PER_MINUTE = 150;

function buildTurnClips(content: PodcastContent): AudioClip[] {
  const turns = Array.isArray(content.turns) ? content.turns : [];
  const units = Array.isArray(content.textUnits) ? content.textUnits : [];
  const unitsBySequence = new Map<number, TextUnit>();
  for (const unit of units) unitsBySequence.set(unit.sequenceNo, unit);

  return turns.map((turn, index) => {
    const unit = unitsBySequence.get(index) ?? units[index];
    return {
      audioUrl: unit?.audioUrl ?? turn.audioUrl ?? null,
      text: unit?.textContent ?? turn.text ?? ''
    };
  });
}

function estimateMinutes(content: PodcastContent): number {
  const wordCount = (content.turns ?? []).reduce(
    (sum, turn) => sum + (turn.text ? turn.text.trim().split(/\s+/).length : 0),
    0
  );
  return Math.max(1, Math.round(wordCount / APPROX_WORDS_PER_MINUTE));
}

export function PodcastPlayer({ module }: { module: EducateModule }) {
  const { storyline, units, setCurrentPosition, finishModule, interactions, currentIndex } = module;
  const content = storyline?.content as PodcastContent | undefined;
  const showLogo = storyline?.uiContent.uiConfig?.showKnolskapeLogo ?? true;

  const [view, setView] = useState<'script' | 'waveform'>('script');
  const cardRefs = useRef<Array<HTMLDivElement | null>>([]);

  const clips = useMemo(() => (content ? buildTurnClips(content) : []), [content]);

  const hostVoice = content?.voiceAssignments?.host;
  const expertVoice = content?.voiceAssignments?.expert;

  const markTurnHeard = useCallback(
    (turnIndex: number) => {
      const unitId = units[turnIndex]?.id;
      if (!unitId) return;
      setCurrentPosition(turnIndex, unitId);
    },
    [setCurrentPosition, units]
  );

  const onAllEnded = useCallback(() => {
    const allUnitIds = units.map((unit) => unit.id);
    finishModule(allUnitIds, interactions);
  }, [finishModule, interactions, units]);

  const playback = useAudioPlayback({
    clips,
    initialIndex: currentIndex,
    onIndexChange: markTurnHeard
  });

  const turns = Array.isArray(content?.turns) ? content!.turns : [];
  const total = turns.length;

  useEffect(() => {
    const activeCard = cardRefs.current[playback.currentIndex];
    activeCard?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [playback.currentIndex]);

  if (!content || total === 0) {
    return (
      <EducateNavHeader
        eyebrow="Micro Podcast"
        title={storyline?.uiContent.title ?? '4E Educate'}
        showLogo={showLogo}
      >
        <main className="w-full px-[15%] py-12">
          <ErrorScreen message="This podcast has no turns to play." />
        </main>
      </EducateNavHeader>
    );
  }

  const minutes = estimateMinutes(content);

  return (
    <EducateNavHeader
      eyebrow="Micro Podcast"
      title={storyline?.uiContent.title ?? '4E Educate'}
      showLogo={showLogo}
    >
      <main className="flex w-full flex-1 flex-col px-[15%] pt-6" data-testid="podcast-screen">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-5 text-base">
            <span className="flex items-center gap-2">
              <span className="inline-block h-2.5 w-2.5 rounded-full bg-brand-600" />
              <span className="font-semibold text-slate-700">Host</span>
              <span className="text-slate-500">— {hostVoice?.voiceName ?? 'Host'}</span>
            </span>
            <span className="flex items-center gap-2">
              <span className="inline-block h-2.5 w-2.5 rounded-full bg-amber-400" />
              <span className="font-semibold text-slate-700">Expert</span>
              <span className="text-slate-500">— {expertVoice?.voiceName ?? 'Expert'}</span>
            </span>
          </div>

          <div className="flex overflow-hidden rounded-lg border border-slate-200">
            {(['script', 'waveform'] as const).map((mode) => (
              <button
                key={mode}
                type="button"
                onClick={() => setView(mode)}
                className={
                  view === mode
                    ? 'bg-brand-600 px-3.5 py-1.5 text-sm font-semibold capitalize text-white'
                    : 'bg-white px-3.5 py-1.5 text-sm font-medium capitalize text-slate-500 hover:bg-slate-50'
                }
              >
                {mode}
              </button>
            ))}
          </div>
        </div>

        {view === 'script' ? (
          <div className="mt-6 flex-1 space-y-3 px-1 pb-40">
            {turns.map((turn, index) => {
              const isActive = index === playback.currentIndex;
              const isHost = turn.speaker === 'host';
              return (
                <div
                  key={index}
                  ref={(node) => {
                    cardRefs.current[index] = node;
                  }}
                  onClick={() => playback.seekTo(index)}
                  style={
                    isActive
                      ? { backgroundColor: ACTIVE_BG, borderColor: ACTIVE_BORDER }
                      : undefined
                  }
                  className={
                    isActive
                      ? 'cursor-pointer rounded-xl border-2 p-5 shadow-sm transition'
                      : 'cursor-pointer rounded-xl border-2 border-transparent bg-white p-5 shadow-sm transition hover:border-slate-200'
                  }
                >
                  <div className="flex items-center gap-2">
                    <span
                      className={
                        isHost
                          ? 'flex h-7 w-7 items-center justify-center rounded-full bg-brand-600 text-white'
                          : 'flex h-7 w-7 items-center justify-center rounded-full bg-amber-400 text-white'
                      }
                    >
                      {isHost ? <Mic size={15} /> : <GraduationCap size={15} />}
                    </span>
                    <span className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">
                      {isHost ? 'Host' : 'Expert'}
                    </span>
                    {isActive && playback.isPlaying && <WaveformIcon />}
                  </div>
                  <p className="mt-3 text-lg leading-relaxed text-slate-700">{turn.text}</p>
                </div>
              );
            })}
          </div>
        ) : (
          <WaveformView
            hostName={hostVoice?.voiceName ?? 'Host'}
            expertName={expertVoice?.voiceName ?? 'Expert'}
            currentSpeaker={turns[playback.currentIndex]?.speaker === 'host' ? 'host' : 'expert'}
            isPlaying={playback.isPlaying}
          />
        )}
      </main>

      <div className="sticky bottom-0 z-20 border-t border-slate-200 bg-white px-[15%] py-4">
        <AudioTransport
          variant="slides"
          isPlaying={playback.isPlaying}
          progress={playback.progress}
          onToggle={playback.toggle}
          onPrev={playback.prev}
          onNext={playback.next}
          prevDisabled={playback.currentIndex <= 0}
          nextDisabled={playback.currentIndex >= total - 1}
          onFinish={onAllEnded}
          isLast={playback.currentIndex >= total - 1}
          finishEnabled={playback.lastClipEnded}
          leftCaption={
            <span className="font-semibold text-slate-600">
              {playback.isPlaying || playback.currentIndex > 0
                ? `${playback.currentIndex + 1} / ${total}`
                : `${total} turns`}
            </span>
          }
          rightCaption={<span className="text-slate-400">~{minutes} min</span>}
        />
      </div>
    </EducateNavHeader>
  );
}

function WaveformView({
  hostName,
  expertName,
  currentSpeaker,
  isPlaying
}: {
  hostName: string;
  expertName: string;
  currentSpeaker: 'host' | 'expert';
  isPlaying: boolean;
}) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-8 pb-40">
      <p className="text-sm font-medium text-slate-400">
        {isPlaying
          ? `${currentSpeaker === 'host' ? hostName : expertName} speaking…`
          : 'Press play to begin'}
      </p>
      <div className="grid w-full max-w-3xl grid-cols-2 gap-6">
        <WaveformCard
          label="Host"
          name={hostName}
          color="#6366f1"
          active={isPlaying && currentSpeaker === 'host'}
        />
        <WaveformCard
          label="Expert"
          name={expertName}
          color="#d6b34a"
          active={isPlaying && currentSpeaker === 'expert'}
        />
      </div>
    </div>
  );
}

function WaveformCard({
  label,
  name,
  color,
  active
}: {
  label: string;
  name: string;
  color: string;
  active: boolean;
}) {
  const barCount = 44;
  const bars = Array.from({ length: barCount }, (_, barIndex) => {
    const envelope = Math.sin((barIndex / (barCount - 1)) * Math.PI);
    const ripple = 0.35 + 0.65 * Math.abs(Math.sin(barIndex * 1.7));
    return {
      height: Math.max(0.15, envelope * ripple),
      duration: 0.5 + Math.abs(Math.sin(barIndex * 2.3)) * 0.5,
      delay: Math.abs(Math.sin(barIndex * 0.9)) * 0.7,
      low: 0.15 + Math.abs(Math.sin(barIndex * 3.1)) * 0.35
    };
  });

  return (
    <div className="flex flex-col items-center gap-3">
      <div
        className="flex h-40 w-full items-center justify-center gap-[3px] rounded-2xl bg-white px-6 shadow-sm transition"
        style={
          active ? { border: `2px solid ${ACTIVE_BORDER}` } : { border: '2px solid transparent' }
        }
      >
        {bars.map((bar, barIndex) => (
          <span
            key={barIndex}
            className="w-[3px] rounded-full"
            style={
              {
                height: `${Math.round(bar.height * 72)}px`,
                backgroundColor: color,
                opacity: active ? 0.9 : 0.35,
                transformOrigin: 'center',
                animation: active ? `podcastWave ${bar.duration}s ease-in-out infinite` : 'none',
                animationDelay: `${bar.delay}s`,
                '--wave-low': bar.low
              } as CSSProperties
            }
          />
        ))}
      </div>
      <span className="flex items-center gap-2 text-sm text-slate-500">
        <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: color }} />
        <span className="font-medium text-slate-600">{label}</span>
        <span className="text-slate-400">— {name}</span>
      </span>
      <style>{`@keyframes podcastWave { 0%,100% { transform: scaleY(var(--wave-low, 0.4)); } 50% { transform: scaleY(1); } }`}</style>
    </div>
  );
}

function WaveformIcon() {
  return (
    <span className="ml-1 flex items-end gap-0.5" aria-hidden="true">
      {[0, 1, 2].map((barIndex) => (
        <span
          key={barIndex}
          className="w-0.5 rounded-full bg-brand-600"
          style={{
            height: '12px',
            animation: 'audioWave 0.9s ease-in-out infinite',
            animationDelay: `${barIndex * 0.15}s`
          }}
        />
      ))}
      <style>{`@keyframes audioWave { 0%,100% { transform: scaleY(0.4); } 50% { transform: scaleY(1); } }`}</style>
    </span>
  );
}
